import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Archive, ArchiveRestore, MapPin, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { vendorsService } from '@/services/vendors';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Vendor } from '@/types/vendors';
import { VendorAddressesSection } from './VendorAddressesSection';

interface Props {
  vendor: Vendor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (vendor: Vendor) => void;
}

export function VendorDetailSheet({ vendor, open, onOpenChange, onEdit }: Props) {
  const qc = useQueryClient();

  const archive = useMutation({
    mutationFn: () => vendorsService.archive(vendor!.id),
    onSuccess: () => {
      toast.success('Vendor archived');
      qc.invalidateQueries({ queryKey: ['vendors'] });
      onOpenChange(false);
    },
  });

  const restore = useMutation({
    mutationFn: () => vendorsService.update(vendor!.id, { status: 'ACTIVE' }),
    onSuccess: () => {
      toast.success('Vendor restored');
      qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{vendor?.name ?? 'Vendor'}</DialogTitle>
            {vendor && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                >
                  <Building2 className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>{vendor.code}</DialogEyebrow>
                  <h2 className="text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {vendor.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant={vendor.status === 'ACTIVE' ? 'success' : 'muted'}
                      className="text-[10px]"
                    >
                      {vendor.status === 'ACTIVE' ? 'active' : 'archived'}
                    </Badge>
                    {vendor.gstin && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        GSTIN
                      </Badge>
                    )}
                    {vendor.pan && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        PAN
                      </Badge>
                    )}
                    {vendor.externalRef && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        ref {vendor.externalRef}
                      </Badge>
                    )}
                    {vendor.email && (
                      <span className="text-[11px] text-muted-foreground">
                        · {vendor.email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {vendor.status === 'ACTIVE' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => archive.mutate()}
                      disabled={archive.isPending}
                    >
                      <Archive className="h-3.5 w-3.5" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restore.mutate()}
                      disabled={restore.isPending}
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                      Restore
                    </Button>
                  )}
                  {onEdit && (
                    <Button variant="outline" size="sm" onClick={() => onEdit(vendor)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat
              label="Vendor since"
              value={vendor ? fmt.date(vendor.createdAt) : '—'}
              accent
            />
            <Stat
              label="Last update"
              value={vendor ? fmt.relative(vendor.updatedAt) : '—'}
              muted
            />
            <Stat label="Code" value={vendor?.code ?? '—'} muted />
          </div>

          <Tabs defaultValue="overview" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="overview" className={DIALOG_TAB_TRIGGER}>
                  Overview
                </TabsTrigger>
                <TabsTrigger value="addresses" className={DIALOG_TAB_TRIGGER}>
                  <MapPin className="h-3.5 w-3.5" />
                  Addresses
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                <TabsContent value="overview" className="m-0 space-y-5">
                  <section className="space-y-3">
                    <SectionHeading title="Identity" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Code" value={<span className="font-mono">{vendor?.code}</span>} />
                      <DetailField label="Name" value={vendor?.name} />
                      <DetailField label="External reference" value={vendor?.externalRef ?? '—'} />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Contact" />
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField label="Email" value={vendor?.email ?? '—'} />
                      <DetailField label="Phone" value={vendor?.phone ?? '—'} />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Tax (India)" />
                    <div className="grid grid-cols-2 gap-4">
                      <DetailField
                        label="GSTIN"
                        value={vendor?.gstin ? <span className="font-mono">{vendor.gstin}</span> : '—'}
                      />
                      <DetailField
                        label="PAN"
                        value={vendor?.pan ? <span className="font-mono">{vendor.pan}</span> : '—'}
                      />
                    </div>
                  </section>
                </TabsContent>

                <TabsContent value="addresses" className="m-0 space-y-4">
                  <SectionHeading
                    title="Address book"
                    hint="Defaults drive purchase order + receipt fields"
                  />
                  <VendorAddressesSection vendorId={vendor?.id ?? null} />
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}
